import { Href } from 'expo-router';

/**
 * Bare pathname for `ROUTES.LIBRARY_CHAT_RESULT`, factored out so the route
 * object, `ACCOUNT_ONLY_UNDER_BROWSE` and `SECURE_ROUTES_WITHOUT_NAV_BAR`
 * below all read the same literal rather than three copies that could drift.
 */
const LIBRARY_CHAT_RESULT_PATH = '/community/ask-result';

export const ROUTES = {
  ACCOUNT: '/account',
  ACCOUNT_INVITES: '/account/invites',
  ACCOUNT_POLLS: '/account/polls' as Href,
  ACCOUNT_POLL: (surveyGroupId: string) => `/account/polls/${surveyGroupId}` as Href,
  ACCOUNT_QR_CODE: '/account/qr-code' as Href,
  ACCOUNT_CONTACT_DETAILS: '/account/contact-details' as Href,
  ACCOUNT_CONTACT_DETAILS_EDIT: '/account/contact-details/edit' as Href,
  COMMUNITY: '/community',
  LIBRARY_TAB: '/(secure)/(tabs)/community' as Href,
  LIBRARY_TAB_CONTENT_POSTS: '/(secure)/(tabs)/community?tab=contentLibrary' as Href,
  COMMUNITY_POST: (postId: number) => `/community/${postId}` as Href,
  COMMUNITY_SHARED_PATH: '/community/shared',
  CONSENT_PATH: '/consent',
  LIBRARY_CHAT: '/community/ask',
  LIBRARY_CHAT_CONVERSATION: (conversationId: string) => `/community/ask/${conversationId}` as Href,
  /**
   * The compact-layout screen for a selected Research result, pushed on every
   * platform so back returns to the conversation; on iOS its Save toggle is a
   * native `Stack.Toolbar` button. Desktop web keeps the side pane. The route
   * never needs the live conversation snapshot, so passing ids as params
   * avoids relying on `AskConversationProvider`, which a pushed screen sits
   * outside — but the turn's results (post titles included) travel through
   * `setPendingResults`/`takePendingResults` instead of a param. Route params
   * reach Segment as screen properties (see `util/deepLinkAnalytics.ts`'s
   * `redactScreenParams`), a deny list of credential-shaped names with no way
   * to know a results param carries inquiry titles rather than something safe
   * to report — so they must never travel in the URL at all.
   *
   * The path is a sibling of `/community/ask`, not a child, on purpose — see
   * the route file's own comment — so it needs its own exact entry in
   * `ACCOUNT_ONLY_UNDER_BROWSE`/`SECURE_ROUTES_WITHOUT_NAV_BAR` below rather
   * than inheriting `LIBRARY_CHAT`'s prefix match.
   */
  LIBRARY_CHAT_RESULT: (params: {
    conversationId: string;
    turnId: string;
    resultId: string;
    source: string;
  }): Href => ({
    pathname: LIBRARY_CHAT_RESULT_PATH,
    params: {
      conversationId: params.conversationId,
      turnId: params.turnId,
      resultId: params.resultId,
      source: params.source,
    },
  }),
  COMMUNITY_SHARED: (token: string) => `/community/shared/${token}` as Href,
  CONFIRM_ACCOUNT_DETAILS: '/confirm-account-details',
  CONTENT_LIBRARY: '/content-library',
  CONTENT_LIBRARY_SHARED_PATH: '/content-library/shared',
  CONTENT_LIBRARY_SHARED: (token: string) => `/content-library/shared/${token}` as Href,
  CREATE_ACCOUNT: '/create-account',
  CREATE_ACCOUNT_STEP_2: '/create-account/step-2',
  DIRECTORY: '/directory',
  FELLOWS: '/fellows',
  DIRECTORY_TAB: '/(secure)/(tabs)/directory' as Href,
  EMAIL_LINK: '/email-link',
  EXPERT_LINK: (slug: string) => `/ask/${slug}` as Href,
  EXPERT_LINK_PATH: '/ask',
  FORGOT_PASSWORD: '/forgot-password',
  INBOX: '/inbox',
  INBOX_CHAT: '/inbox/chat/[id]',
  INBOX_COMPOSE: '/inbox/compose' as Href,
  INBOX_REPLY_DRAFT: '/(secure)/inbox/chat/reply-draft',
  INQUIRY: '/inquiry',
  INQUIRY_STACKED: '/inquiry/new' as Href,
  LOGIN: '/login',
  LOGOUT: '/logout',
  SMART_CALLBACK: '/auth/smart',
  SMART_ENDED: '/auth/smart/ended',
  SMART_ERROR: '/auth/smart/error',
  SMART_LAUNCH: '/auth/smart/launch',
  MEDICAL_ON_DEMAND: '/medical-on-demand',
  MEDICAL_ON_DEMAND_INQUIRY: (teamId: string) => `/medical-on-demand/inquiries/${teamId}` as Href,
  NEW_PASSWORD: '/new-password',
  NEW_QUESTION: '/new-question',
  NEW_QUESTION_GET_CONTACT_INFO: '/(open)/new-question/get-contact-information',
  PRIMUM_SETTINGS: '/(secure)/primum-settings',
  PROFILE: '/profile',
  PROFILE_EDIT: '/profile/edit',
  PROFILE_ME: '/profile/me',
  SURVEY: (surveyId: string, options?: { preview?: boolean }): Href =>
    options?.preview
      ? { pathname: '/survey/[id]', params: { id: surveyId, preview: 'true' } }
      : (`/survey/${surveyId}` as Href),
  SURVEY_PATH_PREFIX: '/survey/',
  UPDATE_PASSWORD: '/update-password',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Routes that have a public twin in the open group.
 *
 * A signed-out visitor who lands on the secure copy of one of these can simply be shown
 * the public version, so a link to it is not worth interrupting anyone for. Every other
 * authenticated route renders nothing at all without an account, which is why a link to
 * one asks the visitor to sign in rather than dropping them somewhere unrelated.
 *
 * This is the single list. It previously existed three times — twice byte-identical under
 * two different names, in the two layouts that each decide which twin a visitor gets —
 * and the copies are what let the two sides of that decision drift apart. Anything that
 * needs to know whether a route survives without an account reads it from here.
 */
export const PUBLIC_BROWSE_ROUTES = [
  ROUTES.COMMUNITY,
  ROUTES.CONTENT_LIBRARY,
  ROUTES.DIRECTORY,
  ROUTES.INQUIRY,
  ROUTES.MEDICAL_ON_DEMAND,
] as const;

/**
 * The exact shape of an expert profile viewed by id, which has a real public twin at
 * `(open)/profile/[id].tsx` — used only to decide whether the secure layout can redirect a
 * signed-out visitor straight to that twin (`/(open)${pathname}`). A deeper profile route
 * (`/profile/:id/about`, work history, disclosures, …) has no such twin, so it deliberately
 * does not match here even though it is still profile-shaped; sending it through this
 * redirect would land on a page the open group has never registered.
 *
 * `/profile/me` matches by design: the open group's `[id].tsx` already resolves a literal
 * `me` id without firing the bad request a signed-out visitor would otherwise trigger, and
 * routing it there is what a signed-out visit to `/profile/me` is tested against.
 */
const PUBLIC_PROFILE_VIEW_PATTERN = /^\/profile\/[^/]+\/?$/;

export function isPublicProfileView(pathname: string): boolean {
  return PUBLIC_PROFILE_VIEW_PATTERN.test(pathname);
}

/**
 * Tab roots that also have a public page of the same path in the open group.
 *
 * A fresh load of one of these paths resolves to the secure tab, not the public page: the tab
 * sits in a route group of its own, and the router prefers the deeper match when no current
 * screen breaks the tie. Before the tabs were grouped the public page won, so the secure
 * layout sends a signed-out visitor on one of these straight to its twin to keep that. The
 * hidden content library tab is not grouped, so its public page still wins and it is not here.
 */
const PUBLIC_TAB_ROOTS: readonly string[] = [ROUTES.DIRECTORY, ROUTES.COMMUNITY];

/** Accepts a bare pathname or a `path?query` destination. */
export function isPublicTabRoot(destination: string): boolean {
  return PUBLIC_TAB_ROOTS.includes(destination.split('?')[0] ?? '');
}

/**
 * Exact-or-path-prefix membership, with any query string discarded first.
 *
 * Shared so the route predicates in this file cannot drift apart on what "on the list"
 * means. Prefix matching is bounded by a separator, so `/directory-admin` is not a match
 * for `/directory` — the distinction `includes` loses.
 */
function matchesAnyRoute(destination: string, routes: readonly string[]): boolean {
  const pathname = destination.split('?')[0] ?? '';

  return routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/**
 * Routes that sit beneath a public-browse prefix but need an account and a verified NPI.
 * Prefix matching would otherwise open them to signed-out visitors and unverified readers
 * alike, and both would reach a screen whose API refuses them.
 */
const ACCOUNT_ONLY_UNDER_BROWSE: readonly string[] = [
  ROUTES.LIBRARY_CHAT,
  LIBRARY_CHAT_RESULT_PATH,
];

function isAccountOnlyUnderBrowse(destination: string): boolean {
  return matchesAnyRoute(destination, ACCOUNT_ONLY_UNDER_BROWSE);
}

/**
 * Whether a destination names one of the public-browse routes, or something beneath one.
 *
 * Matched exactly or as a path prefix, never as a substring. `includes` is the obvious
 * spelling and it admits any route whose name merely *contains* one of these — a future
 * `/expert-directory` or `/my-inquiry-drafts` would read as public browsing and open itself
 * to a reader awaiting NPI verification, with nothing to point at the mistake. The two
 * matchers agree on every route the app has today, so this closes the hole before it opens
 * rather than fixing a live one.
 *
 * Accepts a bare pathname or a `path?query` destination, which is the same contract as
 * `isPubliclyBrowsableRoute` below. Both callers pass `usePathname()` and so never carry a
 * query today, but two exported predicates a rename apart cannot disagree about what they
 * take: under prefix matching a query string is not a near miss, it is a silent `false` for
 * a route that is on the list.
 */
export function isPublicBrowseRoute(destination: string): boolean {
  return (
    !isAccountOnlyUnderBrowse(destination) && matchesAnyRoute(destination, PUBLIC_BROWSE_ROUTES)
  );
}

/**
 * Open-group routes a signed-in reader is left on rather than forwarded off.
 *
 * The open group forwards a signed-in reader into the secure app, and this is the set of
 * exceptions to that. They are exceptions for four unrelated reasons, which is why the
 * list reads as a jumble until you know them: a share link is meant to render for anyone
 * holding the token; an expert link and the public profile resolve their own sign-in state
 * and would lose a recorded open if something forwarded them first; the account-linking,
 * signup and password screens navigate themselves once the reader finishes, so moving them
 * drops what they were carrying; and product-support handles auth inline on the page.
 *
 * `/logout` is on the list for the third reason and is the sharpest case of it. Without it the forward
 * fires while the reader is mid-sign-out — measured, not theorised — and sign-out only
 * appears to work because the secure group bounces the by-then-signed-out reader back. Three
 * navigations to do one, and which of them wins is a race.
 *
 * Membership means *this route and everything under it*, so a nested route needs no entry of
 * its own. Several entries name a path with no route of its own, which is deliberate: the
 * list describes a region of the address space, not a screen.
 */
export const OPEN_ROUTES_A_SIGNED_IN_READER_KEEPS = [
  ROUTES.COMMUNITY_SHARED_PATH,
  ROUTES.CONSENT_PATH,
  ROUTES.CONTENT_LIBRARY_SHARED_PATH,
  ROUTES.CREATE_ACCOUNT_STEP_2,
  ROUTES.EXPERT_LINK_PATH,
  ROUTES.LOGIN,
  ROUTES.LOGOUT,
  ROUTES.MEDICAL_ON_DEMAND,
  ROUTES.NEW_PASSWORD,
  ROUTES.NEW_QUESTION,
  ROUTES.PROFILE,
] as const;

/**
 * Whether a signed-in reader stays on this open-group destination instead of being
 * forwarded into the secure app.
 *
 * Same matching contract as the predicates above: exact or path-prefix, query stripped,
 * never substring. This used to be ten early returns inside the open layout's redirect
 * chain, three of which matched with `includes`. Control flow hid what they had in common,
 * and the one route missing from them was invisible for the same reason.
 */
export function mayStaySignedInOnOpenRoute(destination: string): boolean {
  return matchesAnyRoute(destination, OPEN_ROUTES_A_SIGNED_IN_READER_KEEPS);
}

/**
 * Open-group routes that render without the public nav bar.
 *
 * The auth flow is the one part of the open group that is a task rather than a place: the
 * reader is part-way through signing in, creating an account or recovering a password, and
 * the nav bar offers ways out of it that lose what they have entered.
 */
const OPEN_ROUTES_WITHOUT_NAV_BAR = [
  ROUTES.CREATE_ACCOUNT,
  ROUTES.EMAIL_LINK,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.LOGIN,
  ROUTES.SMART_CALLBACK,
] as const;

export function hidesOpenNavBar(destination: string): boolean {
  return matchesAnyRoute(destination, OPEN_ROUTES_WITHOUT_NAV_BAR);
}

/**
 * Secure-group routes that render without the web nav bar on a compact width.
 *
 * These are full-screen workspaces on a phone-sized viewport, where the nav bar would
 * only take height from a layout that needs all of it. The caller in
 * `app/(secure)/_layout.tsx` additionally gates this on compact width: desktop web has
 * room for both, and hiding the bar there would leave no way back to the rest of the app.
 */
const SECURE_ROUTES_WITHOUT_NAV_BAR = [ROUTES.LIBRARY_CHAT, LIBRARY_CHAT_RESULT_PATH] as const;

export function hidesSecureNavBar(destination: string): boolean {
  return matchesAnyRoute(destination, SECURE_ROUTES_WITHOUT_NAV_BAR);
}

/**
 * Open-group routes that name the same path in the secure group.
 *
 * A signed-in reader on one of these is forwarded to the secure copy of the page they asked
 * for, rather than to the directory — the open group is the public twin of these routes, not
 * a different destination. Everything else in the open group has no secure counterpart to
 * mirror to, so a forward from there has to pick somewhere.
 *
 * Not `PUBLIC_BROWSE_ROUTES`, which is a different question with a different answer: that
 * list holds product support, which handles auth on the page and is never mirrored, and it
 * omits the inbox, which has no public twin but is still mirrored when a link names it.
 */
const SECURE_TWIN_ROUTES = [
  ROUTES.COMMUNITY,
  ROUTES.CONTENT_LIBRARY,
  ROUTES.DIRECTORY,
  ROUTES.INBOX,
  ROUTES.INQUIRY,
] as const;

export function hasSecureTwinRoute(destination: string): boolean {
  return matchesAnyRoute(destination, SECURE_TWIN_ROUTES);
}

/**
 * Whether a destination shows something to a visitor without an account.
 *
 * Accepts a bare pathname or a `path?query` destination. Profile is matched by prefix here
 * rather than through `isPublicProfileView`'s exact shape: a profile sub-page has no public
 * twin to redirect to, but it is still not worth a sign-in wall — the visitor lands on the
 * public directory instead, carrying the sub-page as the resumed destination. Deliberately
 * not folded into `PUBLIC_BROWSE_ROUTES` above, which the NPI-verification allow-list also
 * reads — that allow-list is meant to hold the profile editor shut for an unverified user,
 * a concern this signed-out-only predicate does not share.
 */
export function isPubliclyBrowsableRoute(destination: string): boolean {
  const pathname = destination.split('?')[0] ?? '';

  return isPublicBrowseRoute(pathname) || matchesAnyRoute(pathname, [ROUTES.PROFILE]);
}
